import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Group,
  Stack,
  Title,
  Paper,
  Alert,
  Text,
  Stepper,
  Center,
  Badge,
  Divider,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle, IconCheck } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import { Specimen, ServiceRequest } from '@medplum/fhirtypes';
import { formatCodeableConcept, formatHumanName } from '@medplum/core';
import { OrderForm } from '../components/OrderForm';
import { ConsentSection } from '../components/ConsentSection';
import { useOrderForm, usePrefillOrderForm } from '../hooks/useOrderForm';
import { useSpecimenActions } from '../hooks/useSpecimenActions';
import { useCreateOrder } from '../hooks/useCreateOrder';
import { getPatientOrPractitionerName } from '../utils/common-helpers';
import { SpecimenDisplayCard } from '../specimens/SpecimenDisplayCard';

export default function OrderCreateStepper() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const medplum = useMedplum();
  const { t } = useTranslation(['lims', 'common']);

  const [activeStep, setActiveStep] = useState(0);
  const [createdOrder, setCreatedOrder] = useState<ServiceRequest | null>(null);
  const [specimens, setSpecimens] = useState<Specimen[]>([]);
  const [selectedSpecimens, setSelectedSpecimens] = useState<Specimen[]>([]);

  const returnUrl = searchParams.get('returnUrl') || '/lims';
  const encounterId = searchParams.get('encounterId') || undefined;
  const patientId = searchParams.get('patientId') || undefined;
  const referrerId = searchParams.get('referrerId') || undefined;

  const { formData, errors, updateField, setFormErrors, setFormData } =
    useOrderForm();

  usePrefillOrderForm(patientId, referrerId, setFormData);

  const { loadSpecimens } = useSpecimenActions();

  const { loading, createOrder } = useCreateOrder({
    medplum,
    formData,
    setFormErrors,
    selectedSpecimens,
    encounterId,
  });

  useEffect(() => {
    const fetch = async () => {
      const filteredSpecimens = await loadSpecimens(
        formData.patient,
        formData.sampleType
      );
      setSpecimens(filteredSpecimens);
    };

    fetch();
  }, [formData.patient, formData.sampleType, loadSpecimens]);

  const handleCreateOrder = async () => {
    const order = await createOrder();
    if (!order) {
      return;
    }
    setCreatedOrder(order);
    setActiveStep(1);
  };

  const handleSkipConsent = () => {
    notifications.show({
      title: t('orderCreate.notifications.consentSkipped', '', { ns: 'lims' }),
      message: t('orderCreate.notifications.consentSkippedMessage', '', {
        ns: 'lims',
      }),
      color: 'blue',
    });
    navigate(returnUrl);
  };

  const handleConsentUpdate = () => {
    notifications.show({
      title: t('success', { ns: 'common' }),
      message: t('orderCreate.notifications.consentComplete', '', {
        ns: 'lims',
      }),
      color: 'green',
    });
    navigate(returnUrl);
  };

  const toggleSpecimen = (specimen: Specimen) => {
    setSelectedSpecimens((prev) =>
      prev.some((s) => s.id === specimen.id)
        ? prev.filter((s) => s.id !== specimen.id)
        : [...prev, specimen]
    );
  };

  const renderStepContent = () => {
    if (activeStep === 0) {
      return (
        <Stack gap="md">
          {selectedSpecimens.length > 0 && (
            <Alert icon={<IconInfoCircle size={16} />} color="green">
              <Text size="sm">
                <strong>
                  {t('orderCreate.linkedSpecimensLabel', { ns: 'lims' })}:
                </strong>{' '}
                {selectedSpecimens.length}{' '}
                {selectedSpecimens.length === 1
                  ? t('orderCreate.linkedSpecimensNote', { ns: 'lims' })
                  : t('orderCreate.linkedSpecimensNotePlural', { ns: 'lims' })}
              </Text>
              <Stack gap="xs" mt="xs">
                {selectedSpecimens.map((specimen) => (
                  <Text key={specimen.id} size="xs" c="dimmed">
                    • {specimen.identifier?.[0]?.value || specimen.id}
                  </Text>
                ))}
              </Stack>
              <Text size="sm" mt="xs">
                {t('orderCreate.linkedSpecimensMessage', { ns: 'lims' })}
              </Text>
            </Alert>
          )}

          <OrderForm
            formData={formData}
            errors={errors}
            onFieldChange={updateField}
          />

          {formData.patient && formData.sampleType && (
            <Stack gap="md">
              <Divider
                label={t(
                  'specimenSelection.availableSpecimensDivider',
                  'Available Specimens',
                  { ns: 'lims' }
                )}
                labelPosition="center"
              />
              {specimens.length === 0 ? (
                <Paper withBorder bg="gray.0" radius="md">
                  <Center py="xl">
                    <Stack align="center" gap="md">
                      <Text size="30px" c="dimmed">
                        🔍
                      </Text>
                      <Stack align="center" gap="xs">
                        <Text fw={500} size="md" c="dimmed">
                          {t(
                            'specimenSelection.noSpecimensFound',
                            'No specimens found',
                            { ns: 'lims' }
                          )}
                        </Text>
                        <Text size="sm" c="dimmed" ta="center">
                          {t(
                            'specimenSelection.noSpecimensForType',
                            'No available specimens of type «{{type}}» for {{context}} {{patientName}}',
                            {
                              ns: 'lims',
                              type: formatCodeableConcept(formData.sampleType),
                              context: t(
                                'specimenSelection.forPatient',
                                'the patient',
                                { ns: 'lims' }
                              ),
                              patientName: formatHumanName(
                                formData.patient?.name?.[0]
                              ),
                            }
                          )}
                        </Text>
                      </Stack>
                    </Stack>
                  </Center>
                </Paper>
              ) : (
                <Stack gap="sm">
                  <Group gap="xs">
                    <Text fw={500}>
                      {t('specimenSelection.title', {
                        patientName: getPatientOrPractitionerName(
                          formData.patient
                        ),
                      })}
                    </Text>
                    <Badge size="sm" variant="light">
                      {specimens.length}
                    </Badge>
                  </Group>
                  <Text c="dimmed" mt={-14}>
                    {t(
                      'specimenSelection.existingSpecimensSameType',
                      'Existing specimens of the patient with the same type',
                      { ns: 'lims' }
                    )}
                  </Text>

                  {specimens.map((specimen) => {
                    const isSelected = selectedSpecimens.some(
                      (s) => s.id === specimen.id
                    );
                    return (
                      <Group key={specimen.id}>
                        <UnstyledButton
                          flex={1}
                          onClick={() => toggleSpecimen(specimen)}
                        >
                          <SpecimenDisplayCard
                            specimen={specimen}
                            patient={formData.patient}
                            bg={isSelected ? 'green.0' : undefined}
                          />
                        </UnstyledButton>
                        {isSelected && <IconCheck color="green" />}
                      </Group>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      );
    }

    if (activeStep === 1) {
      if (!createdOrder) {
        return (
          <Stack gap="md">
            <Alert icon={<IconInfoCircle size={16} />} color="red">
              {t('orderCreate.stepper.orderNotCreated', { ns: 'lims' })}
            </Alert>
          </Stack>
        );
      }

      return (
        <ConsentSection
          serviceRequest={createdOrder}
          patient={formData.patient}
          editMode={true}
          onConsentUpdate={handleConsentUpdate}
        />
      );
    }

    return null;
  };

  return (
    <Stack gap="md" className="p-6">
      <Title order={1}>{t('orderCreate.stepper.title', { ns: 'lims' })}</Title>

      <Paper shadow="sm" p="md" withBorder>
        <Stepper
          active={activeStep}
          onStepClick={setActiveStep}
          allowNextStepsSelect={false}
        >
          <Stepper.Step
            label={t('orderCreate.stepper.orderCreation', { ns: 'lims' })}
            description={t('orderCreate.stepper.orderCreationDesc', {
              ns: 'lims',
            })}
            icon={activeStep > 0 ? <IconCheck size={16} /> : undefined}
          />
          <Stepper.Step
            label={t('orderCreate.stepper.consent', { ns: 'lims' })}
            description={t('orderCreate.stepper.consentDesc', { ns: 'lims' })}
            icon={activeStep > 1 ? <IconCheck size={16} /> : undefined}
          />
        </Stepper>

        <Stack gap="md" mt="md">
          {renderStepContent()}

          <Group justify="space-between" mt="xl">
            <Button
              variant="subtle"
              onClick={() => navigate(returnUrl)}
              disabled={loading}
            >
              {t('orderCreate.cancel', { ns: 'lims' })}
            </Button>

            <Group>
              {activeStep === 0 && (
                <Button onClick={handleCreateOrder} loading={loading}>
                  {t('orderCreate.stepper.createOrder', { ns: 'lims' })}
                </Button>
              )}

              {activeStep === 1 && (
                <Button variant="outline" onClick={handleSkipConsent}>
                  {t('orderCreate.stepper.skipConsent', { ns: 'lims' })}
                </Button>
              )}
            </Group>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
